/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { classNames } from 'primereact/utils';

import {
  RegistryFieldsType,
  RegistryWidgetsType,
  RJSFSchema,
  SubmitButtonProps,
  UiSchema,
  FormContextType,
  ErrorSchema,
  FormValidation
} from '@rjsf/utils';
export type { ErrorSchema, FormContextType, FormValidation, UiSchema } from '@rjsf/utils';

import Form from '@rjsf/primereact';
import validator from '@rjsf/validator-ajv8';
import 'leaflet/dist/leaflet.css';
import styled from 'styled-components';
import { Job, linksDefinition, ProcessDescription } from '../types/processing-schemas.ts';

import { Utils } from '../utils/utils.ts';
import { IChangeEvent } from '@rjsf/core/src/components/Form.tsx';
import { BBoxEditableWidget } from './ExecuteProcessForm/BBoxWidgets/index.ts';
import { SelectWidget } from './ExecuteProcessForm/SelectWidget.tsx';
import { SliderRangeWidget } from './ExecuteProcessForm/SliderRangeWidget.tsx';
import { SliderWidget } from './ExecuteProcessForm/SliderWidget.tsx';
import { SubmitButton } from './ExecuteProcessForm/SubmitButton.tsx';
import { SecretField } from './ExecuteProcessForm/SecretField.tsx';
import { DescriptionFieldTemplate } from './ExecuteProcessForm/DescriptionFieldTemplate.tsx';
import { Message } from 'primereact/message';
import { useTranslation } from 'react-i18next';

import { OverLoading } from 'src/components/OverLoading.tsx';
import { Toast } from 'primereact/toast';

const CForm = styled(Form)`
  .rjsf-field-array {
    h5 {
      font-size: 1.05rem !important;
      font-weight: normal;
    }
  }
`;

type OnImportOGCJob = <T extends Job>(job: T) => Job;

export type CustomValidator = (
  formData: FormContextType,
  errors: FormValidation,
  uiSchema?: UiSchema,
  errorSchema?: ErrorSchema
) => FormValidation;

export interface ExecuteProcessFormProps {
  disabled?: boolean;
  inputs: ProcessDescription['inputs'];
  execution_content?: Record<string, unknown>;
  description?: string;
  className?: string;
  onSubmit?: (data: IChangeEvent) => Promise<boolean>;
  onImportJobExecutionContent?: OnImportOGCJob;
  links: ProcessDescription['links'];
  customValidate?: CustomValidator;
}

const DEFAULT_OGC_BOUND = [0.74707, 43.101, 2.11487, 44.0955];

export const ExecuteProcessForm: React.FC<ExecuteProcessFormProps> = (props) => {
  const { inputs, execution_content, links, description, className, disabled, onImportJobExecutionContent } = props;

  const [formData, setFormData] = useState<Record<string, unknown>>();

  const [isEditable, setIsEditable] = useState<boolean>(false);

  const [isPending, setIsPending] = useState<boolean>(false);

  const errorToast = useRef<Toast>(null);

  const timeoutRef = useRef<number>();

  const { t } = useTranslation();

  useEffect(() => {
    if (execution_content) {
      let exec_content: Record<string, unknown> = {};
      Object.entries(execution_content).forEach(([k, v]) => {
        if (inputs && inputs[k]) {
          exec_content = {
            ...exec_content,
            [k]: v
          };
        }
      });
      if (Object.keys(exec_content).length !== 0) {
        setFormData(exec_content);
        setIsEditable(true);
      }
    }
  }, []);

  const schemaInputs = useMemo(() => {
    const properties: RJSFSchema = {};
    let required: string[] = [];
    Object.entries(inputs).forEach(([k, v]) => {
      const { schema, ...rest } = v;
      const property = { ...schema, ...rest };
      properties[k] = { ...property };
      properties[k] = Utils.injectAddOcMetaData(v, properties[k]);
      if (v.schema.type === 'array') {
        if (v.schema.minItems > 0) {
          required = [...required, k];
        }
      } else {
        if (v.minOccurs && v.minOccurs > 0) {
          required = [...required, k];
        }
      }
    });
    const schema: RJSFSchema = {
      type: 'object',
      required: [...required],
      properties: { ...properties }
    };
    return schema;
  }, [inputs, isEditable]);

  const uiSchema = useMemo(() => {
    const schema: UiSchema = {};
    Object.entries(inputs!).forEach(([k, v]) => {
      const matches = Utils.injectMatchUISchema(v);
      if (matches) {
        schema[k] = matches;
      }
    });
    return schema;
  }, [inputs]);

  const customFields: RegistryFieldsType = { secretField: SecretField };
  const widgets: RegistryWidgetsType = {
    bboxEditableWidget: BBoxEditableWidget,
    sliderRangeWidget: SliderRangeWidget,
    sliderWidget: SliderWidget,
    SelectWidget: SelectWidget
  };

  const onSubmit = async (data: IChangeEvent) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (props.onSubmit) {
      const { formData } = data;
      setIsPending(true);
      setFormData(formData);
      const life = 6000;
      props.onSubmit(data).catch((e) => {
        const msg = e.response?.data?.detail || e.message || t(['notifs.error.description'], 'Operation declined');
        errorToast.current?.show({
          severity: 'error',
          summary: t(['notifs.error.title'], 'Error'),
          detail: msg,
          contentClassName: 'text-left text-color font-semibold',
          className: 'text-left text-color',
          life: life
        });
        timeoutRef.current = setTimeout(() => {
          setIsPending(false);
        }, life);
      });
    }
  };

  const onReset = useCallback(() => {
    if (Object.keys(execution_content || {}).length !== 0) {
      setFormData({ ...execution_content });
    } else {
      let bbox: Record<string, unknown> = {};
      let resetValue: Record<string, unknown> = {};
      Object.entries(inputs).forEach(([name, desc]) => {
        if (Utils.isAntflowLegacyBbox(desc)) {
          bbox = {
            name,
            ...desc
          };
        }
      });
      if (bbox) {
        resetValue = {
          [bbox.name as string]: DEFAULT_OGC_BOUND
        };
      }

      setFormData(resetValue);
    }
  }, [inputs, execution_content]);

  const onImportInputs = (job: Job) => {
    const { execution_content } = onImportJobExecutionContent ? onImportJobExecutionContent(job) : job;
    if (execution_content) {
      setFormData(execution_content);
    }
  };

  const isValidInputs = (entry: ProcessDescription['inputs']) => {
    return Object.keys(entry).length !== 0;
  };

  const monitorUrl = (_links: ExecuteProcessFormProps['links']) => {
    const monitorUrl = _links.find((link) => link.rel === linksDefinition.jobMonitor);
    return monitorUrl ? monitorUrl.href : props.links.length > 0 ? props.links[0].href : '';
  };

  return (
    <OverLoading loading={isPending} className="w-full">
      <Toast
        pt={{
          closeButton: {
            onClick: () => setIsPending(false)
          }
        }}
        position="top-center"
        ref={errorToast}
      />
      <section className={classNames(className, 'flex flex-column w-full text-left')}>
        {description && <div className="mb-4">{description}</div>}
        {!isValidInputs(inputs) && (
          <Message
            severity="warn"
            className="mt-2 text-sm"
            text={
              <div>
                <span className="block text-left text-center">
                  {t(['processes.errors.entity_unprocessable.title', 'this process has no inputs defined'])}&nbsp;&nbsp;
                  <a style={{ all: 'unset' }} href={monitorUrl(links)} target="_blank" rel="noreferrer">
                    <i className="pi pi-external-link cursor-pointer hover:text-primary" />
                  </a>
                </span>
              </div>
            }
          />
        )}
        <CForm
          customValidate={props.customValidate}
          showErrorList={'bottom'}
          className={''}
          schema={schemaInputs}
          disabled={disabled}
          uiSchema={uiSchema}
          noHtml5Validate={true}
          onSubmit={onSubmit}
          focusOnFirstError={true}
          widgets={widgets}
          fields={customFields}
          formData={formData}
          validator={validator}
          templates={{
            DescriptionFieldTemplate: DescriptionFieldTemplate,
            ButtonTemplates: {
              SubmitButton: (Props: SubmitButtonProps) => {
                return <SubmitButton {...Props} onImportOGCJob={onImportInputs} onReset={onReset} />;
              }
            }
          }}
          onChange={(changeEvent) => {
            const { formData } = changeEvent;
            setFormData((prevState) => {
              if (prevState) {
                return Object.assign(prevState, formData);
              }
              return formData;
            });
          }}
        />
        <span className="mt-4 text-sm">{t(['misc.required_field', 'fields marked with (*) are required.'])}</span>
      </section>
    </OverLoading>
  );
};
